import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { fetchFavorites, api, fetchMe, getDigestPref, setDigestPref, sendTestDigest, getReferralInfo, claimBonusPack, listWatchlist, removeWatchlist } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Sparkles, Crown, Copy, ExternalLink, Mail, Send, Gift, Share2, Users, Twitter, Facebook, MessageSquare, BellRing, Trash2 } from "lucide-react";
import StoreLogo from "@/components/StoreLogo";
import { toast } from "sonner";
import { getCopyHistory, clearCopyHistory } from "@/lib/copyHistory";

export default function Dashboard() {
  const { user, loading, startGoogleLogin, openLogin, setUser } = useAuth();
  const [favs, setFavs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [history, setHistory] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [digest, setDigest] = useState({ opt_in: false, last_sent: null });
  const [sendingTest, setSendingTest] = useState(false);
  const [ref, setRef] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);
  const [watched, setWatched] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!user) return;
    fetchFavorites()
      .then(setFavs)
      .finally(() => setFetching(false));
    setHistory(getCopyHistory());
    getDigestPref().then(setDigest).catch(() => {});
    getReferralInfo().then(setRef).catch(() => {});
    listWatchlist().then(setWatched).catch(() => {});
  }, [user]);

  const unwatch = async (store) => {
    try {
      await removeWatchlist(store);
      setWatched((w) => w.filter((x) => x.store !== store));
      toast.success(`Stopped tracking ${store}`);
    } catch {
      toast.error("Couldn't remove");
    }
  };

  const toggleDigest = async (next) => {
    try {
      const r = await setDigestPref(next);
      setDigest((d) => ({ ...d, opt_in: r.opt_in }));
      toast.success(next ? "You'll get a daily digest ✨" : "Digest turned off");
    } catch {
      toast.error("Couldn't update preference");
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const r = await sendTestDigest();
      toast.success(`Sent to ${r.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const copyReferral = async () => {
    if (!ref?.share_url) return;
    await navigator.clipboard.writeText(ref.share_url);
    setCopiedRef(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const shareVia = (channel) => {
    if (!ref?.share_url) return;
    const url = encodeURIComponent(ref.share_url);
    const msg = encodeURIComponent(
      `I've been saving with The Couponer — 14,000+ coupons, updated daily. Sign up with my link and we BOTH get a bonus deal pack:`
    );
    const targets = {
      twitter: `https://twitter.com/intent/tweet?text=${msg}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      sms: `sms:?body=${msg}%20${url}`,
      email: `mailto:?subject=${encodeURIComponent("Free coupons on The Couponer")}&body=${msg}%20${url}`,
    };
    if (targets[channel]) window.open(targets[channel], channel === "sms" || channel === "email" ? "_self" : "_blank", "noopener");
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const r = await claimBonusPack();
      toast.success(`Bonus pack of ${r.deals_sent} deals emailed to you 🎁`);
      const fresh = await getReferralInfo();
      setRef(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  // Handle Stripe redirect back
  useEffect(() => {
    const pro = searchParams.get("pro");
    const sid = searchParams.get("session_id");
    if (pro === "success" && sid) {
      toast.success("Welcome to Couponer Pro 🎉");
      // Poll status endpoint so pro flag flips
      let tries = 0;
      const iv = setInterval(async () => {
        tries++;
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid" || tries > 10) {
            clearInterval(iv);
            const me = await fetchMe().catch(() => null);
            if (me) setUser(me);
            const sp = new URLSearchParams(searchParams);
            sp.delete("pro");
            sp.delete("session_id");
            setSearchParams(sp);
          }
        } catch {}
      }, 1500);
      return () => clearInterval(iv);
    } else if (pro === "cancel") {
      toast("Upgrade cancelled");
      const sp = new URLSearchParams(searchParams);
      sp.delete("pro");
      setSearchParams(sp);
    }
  }, [searchParams, setSearchParams, setUser]);

  const upgrade = async (lookup_key) => {
    setUpgrading(true);
    try {
      const r = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <p className="mono text-xs tracking-widest">LOADING...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="dashboard-signed-out">
        <div className="border-2 border-dashed border-black p-10 md:p-16 bg-white max-w-2xl hard-shadow">
          <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-2">YOUR CLIPPINGS</div>
          <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight mb-4">
            Sign in to keep your deals.
          </h1>
          <p className="text-sm text-[#3a3a38] mb-6 leading-relaxed">
            Sign in with Google to save coupons, submit your own, and get a
            personalized front page.
          </p>
          <Button
            onClick={() => openLogin("signin")}
            data-testid="dashboard-signin-btn"
            className="rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white tracking-[0.2em] px-6 py-6 hard-shadow"
          >
            SIGN IN
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="dashboard-page">
      <section className="border-b-2 border-black pb-6 mb-8">
        <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-1">
          WELCOME BACK
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight">
            {user.name}'s clipboard.
          </h1>
          {user.pro && (
            <span
              className="inline-flex items-center gap-1 border-2 border-[#2d2a32] bg-[#f7c873] text-[#2d2a32] px-3 py-1 rounded-full font-black text-xs tracking-[0.15em]"
              data-testid="pro-badge"
            >
              <Crown className="w-3 h-3" />
              PRO
            </span>
          )}
        </div>
        <div className="mt-3 mono text-xs text-[#5c5c5c]">
          {favs.length} saved deal{favs.length !== 1 ? "s" : ""}
        </div>
      </section>

      {!user.pro && (
        <section
          className="mb-10 border-2 border-[#2d2a32] rounded-2xl p-6 bg-gradient-to-br from-[#fff8ef] to-[#ffe9d6] hard-shadow"
          data-testid="upgrade-cta"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-[#ff6b6b]" />
                <span className="eyebrow text-[11px] text-[#ff6b6b]">COUPONER PRO</span>
              </div>
              <h2 className="serif text-3xl md:text-4xl leading-tight mb-2">
                Get every deal before anyone else.
              </h2>
              <p className="text-sm text-[#3a3a38] leading-relaxed">
                Real-time push alerts on expiring deals, ad-free browsing, a
                daily digest of top savings in your ZIP, and early access to
                new features. Cancel anytime.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <Button
                onClick={() => upgrade("pro_monthly")}
                disabled={upgrading}
                data-testid="upgrade-monthly-btn"
                className="rounded-full bg-[#ff6b6b] hover:bg-[#e85555] text-white tracking-[0.2em] font-bold px-6 py-6 hard-shadow-sm"
              >
                {upgrading ? "OPENING..." : "GO PRO · $4.99/MO"}
              </Button>
              <Button
                onClick={() => upgrade("pro_yearly")}
                disabled={upgrading}
                data-testid="upgrade-yearly-btn"
                variant="outline"
                className="rounded-full border-2 border-[#2d2a32] bg-white hover:bg-[#f7c873] tracking-[0.15em] font-bold text-xs"
              >
                OR $47.88/YEAR · SAVE 20%
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Referral card */}
      {ref && (
        <section
          className="mb-6 border-2 border-[#2d2a32] rounded-2xl p-6 bg-white hard-shadow relative overflow-hidden"
          data-testid="referral-section"
        >
          {/* Decorative gift ribbon */}
          <div className="absolute -top-2 -right-2 w-24 h-24 rotate-12 rounded-full bg-[#ff6b6b]/10 blur-2xl pointer-events-none" />
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-full border-2 border-[#2d2a32] bg-[#f7c873] flex items-center justify-center shrink-0">
                <Gift className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c]">REFER & EARN</div>
                <h2 className="serif text-3xl leading-tight">Share the deals, earn a pack.</h2>
                <p className="text-sm text-[#5c5c5c] mt-1 leading-snug">
                  Send this link to a friend. When they sign up, <strong>both of you</strong> get a curated 5-deal bonus pack emailed instantly.
                </p>

                {/* Invite link */}
                <div className="mt-4 flex items-center gap-2 max-w-lg">
                  <div className="flex-1 flex items-center gap-2 bg-[#fff8ef] border-2 border-dashed border-[#2d2a32] rounded-lg px-3 py-2 min-w-0" data-testid="referral-link-box">
                    <span className="mono text-xs text-[#2d2a32] truncate flex-1" title={ref.share_url}>{ref.share_url}</span>
                  </div>
                  <button
                    onClick={copyReferral}
                    data-testid="referral-copy-btn"
                    className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[11px] tracking-[0.2em] font-bold inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    {copiedRef ? "COPIED" : "COPY"}
                  </button>
                </div>

                {/* Share buttons */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <ShareBtn onClick={() => shareVia("twitter")} icon={Twitter} label="Twitter" bg="#000" testid="share-twitter" />
                  <ShareBtn onClick={() => shareVia("facebook")} icon={Facebook} label="Facebook" bg="#1877F2" testid="share-facebook" />
                  <ShareBtn onClick={() => shareVia("sms")} icon={MessageSquare} label="Text" bg="#7dbb6e" testid="share-sms" />
                  <ShareBtn onClick={() => shareVia("email")} icon={Mail} label="Email" bg="#5c5c5c" testid="share-email" />
                </div>
              </div>
            </div>

            {/* Stats + Claim */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 lg:w-56">
              <div className="border-2 border-[#2d2a32] rounded-xl p-3 bg-[#fff8ef] flex-1" data-testid="referral-count-card">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span className="text-[10px] tracking-[0.2em] font-bold">FRIENDS INVITED</span>
                </div>
                <div className="serif text-4xl leading-none mt-2">{ref.referral_count}</div>
              </div>
              <div
                className={`border-2 border-[#2d2a32] rounded-xl p-3 flex-1 ${ref.bonus_pack_credits > 0 ? "bg-[#fde68a]" : "bg-white"}`}
                data-testid="bonus-packs-card"
              >
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  <span className="text-[10px] tracking-[0.2em] font-bold">BONUS PACKS</span>
                </div>
                <div className="serif text-4xl leading-none mt-2">{ref.bonus_pack_credits}</div>
                {ref.bonus_pack_credits > 0 && (
                  <button
                    onClick={handleClaim}
                    disabled={claiming}
                    data-testid="claim-bonus-btn"
                    className="mt-2 w-full h-8 rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[10px] tracking-[0.2em] font-bold disabled:opacity-50"
                  >
                    {claiming ? "CLAIMING..." : "CLAIM PACK"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Digest email opt-in */}
      <section
        className="mb-10 border-2 border-[#2d2a32] rounded-2xl p-5 bg-white hard-shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
        data-testid="digest-section"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full border-2 border-[#2d2a32] bg-[#f7c873] flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="serif text-2xl leading-tight">Daily Deals Digest</div>
            <p className="text-sm text-[#5c5c5c] mt-1 leading-snug">
              Get 6-8 of the best deals emailed to you every morning, personalized to your ZIP.
              {digest.last_sent && (
                <span className="block text-[11px] mt-1">
                  Last sent: {new Date(digest.last_sent).toLocaleString()}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleSendTest}
            disabled={sendingTest}
            data-testid="digest-send-test-btn"
            className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#4ecdc4] text-[11px] tracking-[0.2em] font-bold inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            {sendingTest ? "SENDING..." : "SEND ME ONE NOW"}
          </button>
          <button
            onClick={() => toggleDigest(!digest.opt_in)}
            data-testid="digest-toggle"
            className={`h-10 px-5 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.2em] font-bold ${
              digest.opt_in ? "bg-[#7dbb6e] text-white" : "bg-white hover:bg-[#f7c873]"
            }`}
          >
            {digest.opt_in ? "SUBSCRIBED ✓" : "SUBSCRIBE"}
          </button>
        </div>
      </section>

      {/* Tracked stores (price-drop alerts) */}
      <section className="mb-10" data-testid="tracked-stores-section">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="serif text-3xl tracking-tight flex items-center gap-2">
            <BellRing className="w-5 h-5" />
            Stores you're tracking
          </h2>
          <Link to="/stores" className="eyebrow text-[10px] text-[#5c5c5c] hover:text-[#ff6b6b]" data-testid="track-more-stores-link">
            + TRACK MORE →
          </Link>
        </div>
        {watched.length === 0 ? (
          <div
            className="border-2 border-dashed border-[#2d2a32]/40 rounded-xl p-6 bg-white"
            data-testid="no-tracked-stores"
          >
            <p className="text-sm text-[#3a3a38] leading-relaxed">
              <strong>Never miss a deal.</strong> Track a store and we'll email you the moment a fresh coupon drops. Head to any{" "}
              <Link to="/stores" className="underline decoration-2 underline-offset-2 text-[#ff6b6b]">store page</Link>{" "}
              and hit <BellRing className="w-3 h-3 inline-block" /> <em>Track this store</em>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {watched.map((w) => (
              <div
                key={w.store}
                data-testid={`tracked-store-${w.store}`}
                className={`border-2 border-[#2d2a32] rounded-xl p-4 flex items-center gap-3 hard-shadow-sm ${
                  w.fresh_deal_count > 0 ? "bg-[#fde68a]" : "bg-white"
                }`}
              >
                <Link to={`/store/${w.slug}`} className="shrink-0" aria-label={`View ${w.store}`}>
                  <StoreLogo store={w.store} storeUrl={null} size={44} />
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/store/${w.slug}`}
                    className="serif text-xl leading-tight font-semibold hover:text-[#ff6b6b] truncate block"
                    data-testid={`tracked-store-name-${w.store}`}
                  >
                    {w.store}
                  </Link>
                  <div className="text-[11px] tracking-wider text-[#5c5c5c] mt-0.5">
                    {w.fresh_deal_count > 0
                      ? <span className="text-[#ff6b6b] font-bold">{w.fresh_deal_count} NEW DEAL{w.fresh_deal_count !== 1 ? "S" : ""}</span>
                      : "No new deals yet — we're watching."}
                  </div>
                </div>
                <button
                  onClick={() => unwatch(w.store)}
                  data-testid={`untrack-${w.store}`}
                  aria-label={`Stop tracking ${w.store}`}
                  className="w-9 h-9 border-2 border-[#2d2a32] rounded-full flex items-center justify-center hover:bg-[#ff6b6b] hover:text-white shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {history.length > 0 && (
        <section className="mb-10" data-testid="copy-history-section">          <div className="flex items-baseline justify-between mb-3">
            <h2 className="serif text-3xl tracking-tight flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Codes you've copied
            </h2>
            <button
              onClick={() => { clearCopyHistory(); setHistory([]); }}
              data-testid="clear-history-btn"
              className="eyebrow text-[10px] text-[#5c5c5c] hover:text-[#ff6b6b]"
            >
              CLEAR
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {history.slice(0, 12).map((h) => (
              <div
                key={h.id + h.ts}
                className="border-2 border-dashed border-[#2d2a32] rounded-lg p-3 bg-white flex items-center justify-between gap-3"
                data-testid={`history-${h.id}`}
              >
                <div className="min-w-0">
                  <div className="serif text-lg font-semibold truncate">{h.store}</div>
                  <div className="mono text-sm font-bold text-[#ff6b6b]">{h.code}</div>
                  <div className="text-[11px] text-[#5c5c5c] truncate">{h.title}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => { navigator.clipboard.writeText(h.code); toast.success("Copied again"); }}
                    data-testid={`recopy-${h.id}`}
                    className="w-9 h-9 border-2 border-[#2d2a32] rounded-full flex items-center justify-center hover:bg-[#f7c873]"
                    aria-label="Copy code again"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  {h.store_url && (
                    <a
                      href={h.store_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 border-2 border-[#2d2a32] rounded-full flex items-center justify-center hover:bg-[#ff6b6b] hover:text-white"
                      aria-label="Visit store"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {fetching ? (
        <div className="mono text-xs tracking-widest">LOADING SAVED DEALS...</div>
      ) : favs.length === 0 ? (
        <div className="border-2 border-dashed border-black p-10 bg-white" data-testid="no-favs-state">
          <p className="serif text-3xl mb-2">Your clipboard is empty.</p>
          <p className="text-xs tracking-wider text-[#5c5c5c] mb-4">
            Tap the heart on any deal to save it here.
          </p>
          <Button
            onClick={() => navigate("/")}
            data-testid="browse-feed-btn"
            className="rounded-none bg-black text-white hover:bg-[#D34836] tracking-[0.2em] hard-shadow-sm"
          >
            BROWSE FEED
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favs.map((c, i) => (
            <CouponCard key={c.id} coupon={c} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShareBtn({ onClick, icon: Icon, label, bg, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="h-9 px-3 border-2 border-[#2d2a32] rounded-full text-white text-[10px] tracking-[0.2em] font-bold inline-flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
      style={{ background: bg }}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}
